/*
 * Copyright (c) 2025 Bytedance, Inc. and its affiliates.
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  Tool,
  ToolCallEngine,
  StreamProcessingState,
  StreamChunkResult,
  ChatCompletionMessageToolCall,
} from '@multimodal/agent-interface';
import { zodToJsonSchema } from '../../utils';
import { getLogger } from '../../utils/logger';

/**
 * Base configuration for tool call engines
 */
export interface ToolCallEngineConfig {
  /** Maximum size of content buffer in characters */
  maxBufferSize?: number;
  /** Maximum number of tool calls to track */
  maxToolCalls?: number;
}

/**
 * Cached tool description to avoid repeated generation
 */
interface CachedToolDescription {
  tools: Tool[];
  description: string;
  hash: string;
}

/**
 * Base class for all Tool Call Engines providing common functionality
 * and performance optimizations
 */
export abstract class ToolCallEngineBase extends ToolCallEngine {
  protected logger = getLogger('ToolCallEngineBase');
  protected config: Required<ToolCallEngineConfig>;
  private toolDescriptionCache: CachedToolDescription | null = null;

  constructor(config: ToolCallEngineConfig = {}) {
    super();
    this.config = {
      maxBufferSize: config.maxBufferSize ?? 1024 * 1024, // 1MB default
      maxToolCalls: config.maxToolCalls ?? 100,
    };
  }

  /**
   * Generate a hash for tools array to enable caching
   */
  private generateToolsHash(tools: Tool[]): string {
    return tools
      .map((tool) => `${tool.name}:${tool.description}`)
      .sort()
      .join('|');
  }

  /**
   * Get cached or generate tool description
   */
  protected getCachedToolDescription(tools: Tool[]): string {
    if (tools.length === 0) return '';

    const hash = this.generateToolsHash(tools);
    
    if (this.toolDescriptionCache?.hash === hash) {
      return this.toolDescriptionCache.description;
    }

    const description = this.generateToolDescription(tools);
    this.toolDescriptionCache = { tools, description, hash };
    
    return description;
  }

  /**
   * Generate tool description - to be implemented by subclasses
   */
  protected abstract generateToolDescription(tools: Tool[]): string;

  /**
   * Format tool parameters with caching for better performance
   */
  protected formatToolParameters(tool: Tool): string {
    const schema = zodToJsonSchema(tool.schema);
    const properties = schema.properties || {};
    const requiredProps = schema.required || [];

    if (Object.keys(properties).length === 0) {
      return 'No parameters required';
    }

    return Object.entries(properties)
      .map(([name, prop]: [string, any]) => {
        const isRequired = requiredProps.includes(name);
        const description = prop.description || 'No description';
        return `- ${name}${isRequired ? ' (required)' : ''}: ${description} (type: ${prop.type})`;
      })
      .join('\n');
  }

  /**
   * Safely truncate buffer if it exceeds maximum size
   */
  protected truncateBuffer(buffer: string): string {
    if (buffer.length <= this.config.maxBufferSize) {
      return buffer;
    }

    this.logger.warn(`Buffer exceeded max size (${this.config.maxBufferSize}), truncating`);
    return buffer.slice(-this.config.maxBufferSize);
  }

  /**
   * Validate and limit tool calls array
   */
  protected validateToolCalls(toolCalls: ChatCompletionMessageToolCall[]): ChatCompletionMessageToolCall[] {
    if (toolCalls.length <= this.config.maxToolCalls) {
      return toolCalls;
    }

    this.logger.warn(`Tool calls exceeded max count (${this.config.maxToolCalls}), limiting`);
    return toolCalls.slice(0, this.config.maxToolCalls);
  }

  /**
   * Create a safe processing state with memory limits
   */
  protected createSafeProcessingState(): StreamProcessingState {
    return {
      contentBuffer: '',
      toolCalls: [],
      reasoningBuffer: '',
      finishReason: null,
    };
  }

  /**
   * Update processing state with safety checks
   */
  protected updateProcessingState(
    state: StreamProcessingState,
    contentDelta: string,
    reasoningDelta: string = ''
  ): void {
    // Update content buffer with truncation
    state.contentBuffer = this.truncateBuffer(state.contentBuffer + contentDelta);
    
    // Update reasoning buffer with truncation
    if (reasoningDelta) {
      state.reasoningBuffer = this.truncateBuffer(state.reasoningBuffer + reasoningDelta);
    }

    // Validate tool calls
    state.toolCalls = this.validateToolCalls(state.toolCalls);
  }

  /**
   * Handle common error scenarios in streaming
   */
  protected handleStreamingError(error: unknown, context: string): StreamChunkResult {
    this.logger.error(`Error in ${context}: ${error}`);
    
    return {
      content: '',
      reasoningContent: '',
      hasToolCallUpdate: false,
      toolCalls: [],
    };
  }

  /**
   * Clear cache - useful for testing or when tools change frequently
   */
  public clearCache(): void {
    this.toolDescriptionCache = null;
  }
}
